import React, { PureComponent } from 'react'
import { createPortal } from 'react-dom'
import { get, put } from './util'
import Panel from './components/Panel'
import Button from './components/Button'

class Config extends PureComponent {
  state = {
    loading: true,
    unifiedProfile: false,
    externalProfile: false,
    profile: '',
    profileURL: '',
    profileUpdateInterval: 86400,
    saving: false,
    notice: null
  }

  componentDidMount () {
    get('admin/info')
      .then(({ unifiedProfile, profile, externalProfile, profileURL, profileUpdateInterval }) => {
        this.setState({
          unifiedProfile,
          profile,
          externalProfile,
          profileURL,
          profileUpdateInterval,
          loading: false
        })
      })
      .catch(e => console.error(e))
  }

  componentWillUnmount () {
    clearTimeout(this.noticeTimeout)
  }

  showNotice = message => {
    this.setState({ notice: message })
    clearTimeout(this.noticeTimeout)
    this.noticeTimeout = setTimeout(() => this.setState({ notice: null }), 5000)
  }

  validateProfile = async () => {
    const response = await fetch('https://services.nssurge.com/v1/config/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile: this.state.profile })
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      const message = payload && payload.error && payload.error.message
        ? payload.error.message
        : 'Validator request failed'
      throw new Error(message)
    }
    return payload
  }

  handleSave = async () => {
    this.setState({ saving: true })
    if (this.state.unifiedProfile && !this.state.externalProfile) {
      try {
        const payload = await this.validateProfile()
        if (!payload.valid) {
          const message = payload && payload.error && payload.error.message
            ? payload.error.message
            : 'Invalid config'
          const proceed = window.confirm(`Config validation failed: ${message}\nContinue saving?`)
          if (!proceed) {
            this.setState({ saving: false })
            return
          }
        } else {
          this.showNotice('Config validation succeeded')
        }
      } catch (e) {
        const message = e && e.message ? e.message : 'Validator request failed'
        const proceed = window.confirm(`Config validation failed: ${message}\nContinue saving?`)
        if (!proceed) {
          this.setState({ saving: false })
          return
        }
      }
    }

    put('admin/info', {
      unifiedProfile: this.state.unifiedProfile,
      profile: this.state.profile,
      externalProfile: this.state.externalProfile,
      profileURL: this.state.profileURL,
      profileUpdateInterval: this.state.profileUpdateInterval
    })
      .then(() => {
        this.setState({ saving: false })
        alert('Saved successfully')
      })
      .catch(e => {
        alert(e.message ? `Failed to save: ${e.message}` : 'Failed to save')
        this.setState({ saving: false })
      })
  }

  render () {
    if (this.state.loading) {
      return <div className='flex items-center justify-center py-20 text-slate-500'>Loading config…</div>
    }

    const notice = this.state.notice
      ? createPortal(
        <div className='config-toast fixed top-20 left-1/2 z-50 -translate-x-1/2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 shadow-sm'>
          <div className='flex items-center gap-2'>
            <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700'>
              <svg viewBox='0 0 20 20' fill='currentColor' className='h-3 w-3'>
                <path
                  fillRule='evenodd'
                  d='M16.704 5.29a1 1 0 0 1 .006 1.415l-7.5 7.571a1 1 0 0 1-1.427-.008L3.29 9.694a1 1 0 1 1 1.42-1.4l3.382 3.432 6.79-6.86a1 1 0 0 1 1.412.004Z'
                  clipRule='evenodd'
                />
              </svg>
            </span>
            <span>{this.state.notice}</span>
          </div>
        </div>,
        document.body
      )
      : null

    return (
      <div className='space-y-4'>
        {notice}
        <Panel>
          <h2 className='text-xl font-semibold text-ink mb-4'>Unified Profile</h2>
          <label className='inline-flex items-center gap-2 text-sm text-slate-700'>
            <input
              type='checkbox'
              className='w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500'
              checked={this.state.unifiedProfile}
              onChange={e => this.setState({ unifiedProfile: e.target.checked })}
            />
            Enable unified profile for all users
          </label>
          {this.state.unifiedProfile && (
            <div className='mt-5 space-y-4'>
              <div className='flex gap-4 text-sm font-semibold'>
                <button
                  className={`pb-1 border-b-2 ${!this.state.externalProfile ? 'border-ink text-ink' : 'border-transparent text-slate-400'}`}
                  onClick={() => this.setState({ externalProfile: false })}
                >
                  Inline Text
                </button>
                <button
                  className={`pb-1 border-b-2 ${this.state.externalProfile ? 'border-ink text-ink' : 'border-transparent text-slate-400'}`}
                  onClick={() => this.setState({ externalProfile: true })}
                >
                  Hosted URL
                </button>
              </div>
              {this.state.externalProfile ? (
                <div>
                  <label className='text-sm font-medium text-slate-600'>Profile URL</label>
                  <input
                    className='mt-2 w-full rounded-2xl border border-slate-200 px-4 py-2.5 focus:border-ink focus:ring-1 focus:ring-ink'
                    value={this.state.profileURL || ''}
                    onChange={e => this.setState({ profileURL: e.target.value })}
                    placeholder='https://'
                  />
                  <p className='text-xs text-slate-500 mt-1'>Use {'{username}'} and {'{passcode}'} placeholders to inject credentials.</p>
                </div>
              ) : (
                <div>
                  <label className='text-sm font-medium text-slate-600'>Profile Content</label>
                  <textarea
                    rows='10'
                    className='mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono text-sm focus:border-ink focus:ring-1 focus:ring-ink'
                    value={this.state.profile || ''}
                    onChange={e => this.setState({ profile: e.target.value })}
                  />
                </div>
              )}
            </div>
          )}
        </Panel>

        {this.state.unifiedProfile && (
          <Panel>
            <label className='block text-sm font-medium text-slate-600'>Update Interval (seconds)</label>
            <input
              type='number'
              className='mt-2 w-40 rounded-2xl border border-slate-200 px-4 py-2.5 focus:border-ink focus:ring-1 focus:ring-ink'
              value={this.state.profileUpdateInterval || ''}
              onChange={e =>
                this.setState({ profileUpdateInterval: Number(e.target.value) || 0 })
              }
            />
          </Panel>
        )}

        <div className='text-right'>
          <Button onClick={this.handleSave} disabled={this.state.saving}>
            {this.state.saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    )
  }
}

export default Config
